"use client";

import { NAVIGATION_MENU, NavigationItem } from "@/lib/constants/navigation";
import { MarketFinderNavBadge } from "@/components/layout/MarketFinderNavBadge";
import { getCurrentYear } from "@/lib/utils";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/lib/auth/store";
import { hasScope, matchesScope } from "@/lib/auth/scope-utils";
import { useIsGenciseUser } from "@/lib/hooks/useIsGenciseUser";
import { ChevronDown, LogOut, Pin, PinOff } from "lucide-react";
import * as LucideIcons from "lucide-react";
import NextImage from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";
import { useSidebar } from "@/lib/hooks/useSidebar";
import { getListFilters } from "@/lib/hooks/usePersistedFilters";
import { Typography } from "@/components/common/ui/Typography";
import { Button } from "@/components/forms/Button";

/** Navigate to href, appending any persisted list filters from sessionStorage. */
function useFilteredNav() {
  const router = useRouter();
  return (e: React.MouseEvent, href: string) => {
    const saved = getListFilters(href);
    if (saved) {
      e.preventDefault();
      router.push(`${href}?${saved}`);
    }
    // If no saved filters, let the default <Link> navigation happen
  };
}

export function Sidebar() {
  const currentYear = getCurrentYear();
  const pathname = usePathname();
  const { isCollapsed: isPinnedCollapsed, toggle } = useSidebar();
  const [isPeeking, setIsPeeking] = useState(false);
  const isCollapsed = isPinnedCollapsed && !isPeeking;
  const revealDelay = isCollapsed ? "delay-0" : "delay-200";
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const isGenciseUser = useIsGenciseUser();
  const handleFilteredNav = useFilteredNav();
  // Explicit open/closed overrides for the top-level sections. Absent an entry,
  // a section follows the current route, so landing on /market-finder from
  // anywhere (a deep link, a redirect) shows its submenu without a second click.
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const isMenuOpen = (item: NavigationItem) =>
    openMenus[item.id] ?? pathname.startsWith(item.href);
  const setMenuOpen = (id: string, open: boolean) =>
    setOpenMenus((prev) => ({ ...prev, [id]: open }));

  // Visibility helper applied uniformly to top-level items, children, and
  // grandchildren. Items flagged `requiresGenciseEmail` are hidden unless the
  // user's email is on the gencise.ai domain (AND-gated with the scope check).
  const isVisible = (item: NavigationItem): boolean => {
    if (item.requiresGenciseEmail && !isGenciseUser) return false;
    if (item.excludeIfScope && hasScope(user?.scopes, item.excludeIfScope)) return false;
    if (item.scope && !matchesScope(user?.scopes, item.scope)) return false;
    return true;
  };

  return (
    <div
      className={cn("relative h-screen shrink-0 transition-all duration-200",
        isPinnedCollapsed ? "w-16" : "w-64"
      )}
      onMouseEnter={() => setIsPeeking(true)}
      onMouseLeave={() => setIsPeeking(false)}
      onFocus={() => setIsPeeking(true)}
      onBlur={() => setIsPeeking(false)}
    >
      <aside
        className={cn("absolute inset-y-0 left-0 z-40 bg-navigator-primary-dark flex flex-col border-r border-navigator-primary/20 transition-all duration-200",
          isCollapsed ? "w-16" : "w-64",
          isPinnedCollapsed && !isCollapsed && "shadow-2xl shadow-black/40"
        )}
      >
        {/* Collapsed, the header stacks: logo on top, toggle underneath. The logo
            survives the collapse — it is the only thing identifying the product at
            64px wide — and only the "IQ Suite" wordmark is squeezed out. */}
        <div className="px-3 py-4">
          <div className={cn("flex overflow-hidden",
            isCollapsed ? "flex-col items-center gap-3" : "items-center justify-between"
          )}>
            <div className={cn("flex items-center overflow-hidden", isCollapsed ? "gap-0" : "gap-3")}>
              <NextImage
                src="/logo-icon.png"
                alt="IQ Suite Logo"
                width={36}
                height={36}
                className={cn("object-contain shrink-0 transition-colors duration-200",
                  isCollapsed ? "bg-white" : "bg-transparent"
                )}
              />
              <div className={cn("overflow-hidden transition-all duration-200", revealDelay,
                isCollapsed ? "max-w-0 opacity-0" : "max-w-[200px] opacity-100"
              )}>
                <Typography variant="headline3" weight="bold" color="white" as="span" className="md:text-2xl whitespace-nowrap">
                  IQ Suite
                </Typography>
              </div>
            </div>
            {!isCollapsed && (
              <Button
                iconOnly
                startIcon={
                  isPinnedCollapsed
                    ? <Pin className="w-5 h-5 text-white/80 hover:text-white" />
                    : <PinOff className="w-5 h-5 text-white/80 hover:text-white" />
                }
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsPeeking(false);
                  toggle();
                }}
                title={isPinnedCollapsed ? "Pin menu open" : "Unpin menu"}
                aria-label={isPinnedCollapsed ? "Pin menu open" : "Unpin menu"}
                className="shrink-0 p-1.5 hover:bg-white/10 border-transparent"
              />
            )}
          </div>
        </div>

        <div className="mb-4 mx-3">
          <div className="h-px bg-linear-to-r from-transparent via-white/20 to-transparent" />
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-2.5">
          {NAVIGATION_MENU.filter(isVisible).map((item) => {
            const Icon = LucideIcons[
              item.icon as keyof typeof LucideIcons
            ] as any;
            const visibleChildren = item.children?.filter((child) => {
              if (!isVisible(child)) return false;
              // If child has its own children, ensure at least one is visible
              if (child.children) {
                const visibleGrandchildren = child.children.filter(isVisible);
                if (visibleGrandchildren.length === 0) return false;
              }
              return true;
            });
            const hasChildren = visibleChildren && visibleChildren.length > 0;

            const isRouteInside = (target: string) =>
              pathname === target || pathname.startsWith(target + "/");
            const childOwnsRoute = visibleChildren?.some(
              (child) =>
                isRouteInside(child.href) ||
                child.children
                  ?.filter(isVisible)
                  .some((gc) => isRouteInside(gc.href)),
            );
            const isSectionActive = isRouteInside(item.href) && !childOwnsRoute;

            return (
              <div key={item.id} className="mb-1">
                {hasChildren ? (
                    <div className="group/menu">
                      {/* A section header does two jobs: it goes to the section's
                          landing page AND reveals its submenu. Toggling only (the
                          old behaviour) left the product itself an extra click
                          away. The chevron stays a pure toggle so an open section
                          can still be collapsed without navigating away. */}
                      <div
                        className={cn("flex items-center rounded-lg transition-all overflow-hidden",
                          (isCollapsed ? isRouteInside(item.href) : isSectionActive)
                            ? "bg-amber-500/20 text-amber-400 border-l-2 border-amber-400"
                            : isRouteInside(item.href)
                              ? "bg-white/10 text-white hover:bg-white/10"
                              : "text-white/90 hover:bg-white/10 hover:text-white"
                        )}
                      >
                        <Link
                          href={item.href}
                          onClick={(e) => {
                            setMenuOpen(item.id, true);
                            handleFilteredNav(e, item.href);
                          }}
                          title={isCollapsed ? item.label : undefined}
                          className="relative flex-1 min-w-0 px-3 py-2.5"
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5 shrink-0" />
                            <Typography
                              variant="body"
                              weight="semibold"
                              color="inherit"
                              as="span"
                              className={cn("whitespace-nowrap transition-all duration-200", revealDelay,
                                isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
                              )}
                            >
                              {item.label}
                            </Typography>
                            {item.id === "market_finder" && (
                              <MarketFinderNavBadge collapsed={isCollapsed} />
                            )}
                          </div>
                        </Link>
                        {!isCollapsed && (
                          <button
                            type="button"
                            onClick={() => setMenuOpen(item.id, !isMenuOpen(item))}
                            aria-expanded={isMenuOpen(item)}
                            aria-label={`${isMenuOpen(item) ? "Collapse" : "Expand"} ${item.label} menu`}
                            className="shrink-0 px-3 py-2.5 cursor-pointer"
                          >
                            <ChevronDown className={cn("w-4 h-4 transition-transform",
                              isMenuOpen(item) && "rotate-180"
                            )} />
                          </button>
                        )}
                      </div>

                      <div className={cn("ml-4 grid transition-[grid-template-rows] duration-200",
                        isMenuOpen(item) && !isCollapsed ? "grid-rows-[1fr] mt-1" : "grid-rows-[0fr]"
                      )}>
                        <div className="overflow-hidden space-y-1">
                        {visibleChildren?.map(
                          (child: NavigationItem, childIndex: number) => {
                            const ChildIcon = LucideIcons[
                              child.icon as keyof typeof LucideIcons
                            ] as any;

                            // Check if child has its own visible children (sub-accordion)
                            const grandchildren = child.children?.filter(isVisible);
                            const hasGrandchildren =
                              grandchildren && grandchildren.length > 0;

                            if (hasGrandchildren) {
                              return (
                                <div key={`${item.id}-${childIndex}`}>
                                  <input
                                    type="checkbox"
                                    id={`menu-${child.id}`}
                                    className="peer hidden"
                                    defaultChecked={pathname.startsWith(
                                      child.href,
                                    )}
                                  />
                                  <label
                                    htmlFor={`menu-${child.id}`}
                                    className={cn("flex items-center justify-between px-3 py-2 rounded-lg transition-colors duration-200 cursor-pointer",
                                      pathname.startsWith(child.href)
                                        ? "text-white"
                                        : "text-white/70 hover:bg-white/10 hover:text-white"
                                    )}
                                  >
                                    <div className="flex items-center gap-3">
                                      <ChildIcon className="w-4 h-4" />
                                      <Typography variant="body" color="inherit" as="span">
                                        {child.label}
                                      </Typography>
                                    </div>
                                    <ChevronDown className="w-3 h-3 transition-transform" />
                                  </label>
                                  <div className="ml-4 grid grid-rows-[0fr] peer-checked:grid-rows-[1fr] peer-checked:mt-1 transition-[grid-template-rows] duration-200">
                                    <div className="overflow-hidden space-y-1">
                                    {grandchildren.map((gc, gcIndex) => {
                                      const GcIcon = LucideIcons[
                                        gc.icon as keyof typeof LucideIcons
                                      ] as any;
                                      const isActive = pathname === gc.href;
                                      return (
                                        <Link
                                          key={`${child.id}-${gcIndex}`}
                                          href={gc.href}
                                          onClick={(e) =>
                                            handleFilteredNav(e, gc.href)
                                          }
                                        >
                                          <div
                                            className={cn("flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-200",
                                              isActive
                                                ? "bg-amber-500/20 text-amber-400 border-l-2 border-amber-400"
                                                : "text-white/70 hover:bg-white/10 hover:text-white"
                                            )}
                                          >
                                            <GcIcon className="w-4 h-4" />
                                            <Typography variant="body" color="inherit" as="span">
                                              {gc.label}
                                            </Typography>
                                          </div>
                                        </Link>
                                      );
                                    })}
                                    </div>
                                  </div>
                                </div>
                              );
                            }

                            const isActive = pathname === child.href;
                            return (
                              <Link
                                key={`${item.id}-${childIndex}`}
                                href={child.href}
                                onClick={(e) => handleFilteredNav(e, child.href)}
                              >
                                <div
                                  className={cn("flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-200",
                                    isActive
                                      ? "bg-amber-500/20 text-amber-400 border-l-2 border-amber-400"
                                      : "text-white/70 hover:bg-white/10 hover:text-white"
                                )}
                                >
                                  <ChildIcon className="w-4 h-4" />
                                  <Typography variant="body" color="inherit" as="span">
                                    {child.label}
                                  </Typography>
                                </div>
                              </Link>
                            );
                          },
                        )}
                        </div>
                      </div>
                    </div>
                ) : (
                  <Link
                    href={item.href}
                    onClick={(e) => handleFilteredNav(e, item.href)}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <div
                      className={cn("relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200 overflow-hidden",
                        pathname === item.href ||
                        pathname.startsWith(item.href + "/")
                          ? "bg-amber-500/20 text-amber-400 border-l-2 border-amber-400"
                          : "text-white/90 hover:bg-white/10 hover:text-white"
                      )}
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                      <Typography
                        variant="body"
                        weight="semibold"
                        color="inherit"
                        as="span"
                        className={cn("whitespace-nowrap transition-all duration-200", revealDelay,
                          isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
                        )}
                      >
                        {item.label}
                      </Typography>
                      {item.id === "market_finder" && (
                        <MarketFinderNavBadge collapsed={isCollapsed} />
                      )}
                    </div>
                  </Link>
                )}
              </div>
            );
          })}
        </nav>

        <div
          className={cn("border-t border-navigator-primary/20", isCollapsed ? "p-2" : "p-4")}
        >
          <div
            className={cn("flex items-center rounded-lg bg-white/5",
              isCollapsed ? "justify-center p-2" : "gap-3 px-3 py-2"
            )}
          >
            <Link
              href="/profile"
              className={cn("flex items-center flex-1 min-w-0 hover:opacity-80 transition-opacity",
                isCollapsed ? "justify-center" : "gap-3 overflow-hidden"
              )}
              title="View profile"
            >
              <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                <Typography as="span" variant="body" weight="medium" color="text-amber-400">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </Typography>
              </div>
              <div className={cn("flex-1 min-w-0 transition-all duration-200", revealDelay,
                isCollapsed ? "max-w-0 opacity-0" : "max-w-[200px] opacity-100"
              )}>
                <Typography variant="body" weight="medium" color="white" as="p" truncate>
                  {user?.name || "My Account"}
                </Typography>
                <Typography variant="small" color="text-white/60" as="p" truncate>
                  {user?.email || ""}
                </Typography>
              </div>
            </Link>
            <Button
              iconOnly
              startIcon={<LogOut className="w-4 h-4 text-white/60 hover:text-white" />}
              variant="ghost"
              size="sm"
              onClick={logout}
              title="Sign out"
              aria-label="Sign out"
              className={cn("shrink-0 p-1.5 hover:bg-white/10 border-transparent transition-all duration-200",
                isCollapsed ? "hidden" : "max-w-10 opacity-100"
              )}
            />
          </div>
        </div>

        <footer className={cn("border-t border-navigator-primary/20 overflow-hidden transition-all duration-200", revealDelay,
          isCollapsed ? "max-h-0 opacity-0 p-0" : "max-h-16 opacity-100 p-4"
        )}>
          <div className="flex items-center justify-center gap-2 whitespace-nowrap">
            <Typography variant="small" color="text-white/40" as="span">© {currentYear}</Typography>
            <Typography variant="small" color="text-white/30" as="span">•</Typography>
            <Typography variant="small" color="text-white/30" as="span">All rights reserved</Typography>
          </div>
        </footer>
      </aside>
    </div>
  );
}
